import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BracketRoundView } from './tournament-bracket-page.models';
import { TournamentBracketMatchCardComponent } from './tournament-bracket-match-card.component';

@Component({
  selector: 'app-tournament-bracket-round-column',
  standalone: true,
  imports: [CommonModule, TournamentBracketMatchCardComponent],
  templateUrl: './tournament-bracket-round-column.component.html',
  styleUrl: './tournament-bracket-round-column.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TournamentBracketRoundColumnComponent {
  private static readonly DEFAULT_CARD_HEIGHT_REM = 6.75;
  private static readonly COMPACT_CARD_HEIGHT_REM = 4.25;
  private static readonly DEFAULT_BASE_GAP_REM = 0.75;
  private static readonly COMPACT_BASE_GAP_REM = 0.32;
  private static readonly MAX_GAP_REM = 12;
  private static readonly MAX_OFFSET_REM = 8;

  @Input({ required: true }) round!: BracketRoundView;
  @Input() roundIndex = 0;
  @Input() totalRounds = 1;
  @Input() previousMatchCount: number | null = null;
  @Input() compact = false;
  @Input() explicitMatchOffsetRem: number | null = null;
  @Input() explicitMatchGapRem: number | null = null;
  @Input() outgoingConnectorSpanRem: number | null = null;
  @Input() forceOutgoingConnector = false;
  @Input() hideIncomingConnector = false;
  @Input() joinIncomingConnector = false;
  @Input() inactiveFinalCard = false;
  @Input() inactiveFinalCardLabel: string | null = null;
  @Input() reportScoreHrefByMatchId: ReadonlyMap<number, string> | null = null;
  @Input() reportScoreLabelByMatchId: ReadonlyMap<number, string> | null = null;
  @Input() reportScoreQueryParams: Record<string, string> | null = null;
  @Input() reportScoreLabel = 'Report score';

  protected isLastRound(): boolean {
    return this.roundIndex >= this.totalRounds - 1;
  }

  protected isFirstRound(): boolean {
    return this.roundIndex === 0;
  }

  protected matchOffsetRem(): number {
    if (this.explicitMatchOffsetRem != null) {
      return this.explicitMatchOffsetRem;
    }

    if (this.roundIndex <= 0) return 0;
    const ratio = this.matchRatio();
    if (ratio <= 1) return 0;
    const pitch = this.cardHeightRem() + this.baseGapRem();
    const offset = ((ratio - 1) / 2) * pitch;
    return Math.min(offset, TournamentBracketRoundColumnComponent.MAX_OFFSET_REM);
  }

  protected matchGapRem(): number {
    if (this.explicitMatchGapRem != null) {
      return this.explicitMatchGapRem;
    }

    if (this.round.matches.length <= 1) {
      return this.baseGapRem();
    }

    const ratio = this.matchRatio();
    const pitch = this.cardHeightRem() + this.baseGapRem();
    if (ratio <= 1) {
      return this.baseGapRem();
    }

    const gap = ratio * pitch - this.cardHeightRem();
    return Math.min(gap, TournamentBracketRoundColumnComponent.MAX_GAP_REM);
  }

  protected cardHeightRem(): number {
    return this.compact
      ? TournamentBracketRoundColumnComponent.COMPACT_CARD_HEIGHT_REM
      : TournamentBracketRoundColumnComponent.DEFAULT_CARD_HEIGHT_REM;
  }

  protected connectorSpanRem(): number | null {
    return this.outgoingConnectorSpanRem;
  }

  protected showPairLink(index: number): boolean {
    if (this.isLastRound()) return false;
    if (index % 2 !== 0 || index + 1 >= this.round.matches.length) return false;

    const currentTarget = this.round.matches[index]?.winner_to_match_id ?? null;
    const siblingTarget = this.round.matches[index + 1]?.winner_to_match_id ?? null;

    return currentTarget != null && currentTarget === siblingTarget;
  }

  protected isMatchInPair(index: number): boolean {
    if (index < 0 || index >= this.round.matches.length) return false;

    if (index % 2 === 0) {
      return this.showPairLink(index);
    }

    return this.showPairLink(index - 1);
  }

  protected hideOutgoingConnector(): boolean {
    return this.isLastRound() && !this.forceOutgoingConnector;
  }

  private matchRatio(): number {
    const currentCount = Math.max(1, this.round.matches.length);
    const previousCount = Math.max(currentCount, this.previousMatchCount ?? currentCount);
    return Math.max(1, Math.floor(previousCount / currentCount));
  }

  private baseGapRem(): number {
    return this.compact
      ? TournamentBracketRoundColumnComponent.COMPACT_BASE_GAP_REM
      : TournamentBracketRoundColumnComponent.DEFAULT_BASE_GAP_REM;
  }
}
